/**
 * 課金の計算（純関数のみ）。
 *
 * **金額は最小通貨単位（JPY なら円）の整数だけで扱う。** 浮動小数を一度でも
 * 経由すると、丸め方の違いで請求額が 1 円ずれる。1 円のずれは
 * 「請求書と入金額が合わない → 消し込めない → 44 日目に自動停止」まで直結する。
 *
 * ここに DB も I/O も入れないのは、**金額の正しさを単体テストで固定する**ため。
 * `billing-math.test.ts` が境界（日割り 0 日 / 全日 / 端数 / 負の明細）を押さえる。
 */

/* ------------------------------------------------------------ 税額 */

/** 税率の区分コード。日本の適格請求書は「税率ごとに区分」した記載が要る。 */
export const TAX_CODES = ['standard', 'reduced', 'exempt'] as const;
export type TaxCode = (typeof TAX_CODES)[number];

export interface TaxableLine {
  /** 税抜金額（負もあり得る＝値引き・SLA クレジット）。 */
  readonly amount: number;
  readonly taxCode: TaxCode;
  /** basis point（10% = 1000）。 */
  readonly taxRateBp: number;
}

export interface TaxBucket {
  readonly taxCode: TaxCode;
  readonly taxRateBp: number;
  /** 区分ごとの税抜合計。 */
  readonly taxableAmount: number;
  /** 区分ごとの消費税額。 */
  readonly taxAmount: number;
}

/**
 * 税率ごとに区分した対価と税額を出す（適格請求書の必須記載事項）。
 *
 * **端数処理は「税率ごとに 1 回だけ」**（インボイス制度の要件）。
 * 明細ごとに切り捨てると、明細本数が増えるほど税額が過少になり、
 * 税務調査で「税額の計算方法が不正」と指摘される。
 *
 * 丸めは `Math.floor` ではなく 0 方向への切り捨て（`Math.trunc`）を使う。
 * SLA クレジットで区分合計が負になったとき、`Math.floor(-1.5)` は -2 に
 * なって**返金額が 1 円多くなる**。返す側が損をする丸めを既定にしない。
 */
export function taxBuckets(lines: readonly TaxableLine[]): TaxBucket[] {
  const byCode = new Map<string, { taxCode: TaxCode; taxRateBp: number; taxableAmount: number }>();
  for (const line of lines) {
    if (!Number.isSafeInteger(line.amount)) {
      throw new Error(`税計算に整数以外の金額が渡されました: ${line.amount}`);
    }
    const key = `${line.taxCode}:${line.taxRateBp}`;
    const found = byCode.get(key);
    if (found) found.taxableAmount += line.amount;
    else {
      byCode.set(key, {
        taxCode: line.taxCode,
        taxRateBp: line.taxRateBp,
        taxableAmount: line.amount,
      });
    }
  }
  return [...byCode.values()]
    // 表示順を固定する（請求書 PDF と画面で並びが変わると照合できない）。
    .sort((a, b) => b.taxRateBp - a.taxRateBp || a.taxCode.localeCompare(b.taxCode))
    .map((b) => ({
      taxCode: b.taxCode,
      taxRateBp: b.taxRateBp,
      taxableAmount: b.taxableAmount,
      taxAmount: Math.trunc((b.taxableAmount * b.taxRateBp) / 10000),
    }));
}

export interface InvoiceTotals {
  readonly subtotal: number;
  readonly taxAmount: number;
  readonly total: number;
  readonly buckets: TaxBucket[];
}

/** 請求書の合計。`subtotal` は税抜、`total` は税込。 */
export function invoiceTotals(lines: readonly TaxableLine[]): InvoiceTotals {
  const buckets = taxBuckets(lines);
  const subtotal = lines.reduce((sum, l) => sum + l.amount, 0);
  const taxAmount = buckets.reduce((sum, b) => sum + b.taxAmount, 0);
  return { subtotal, taxAmount, total: subtotal + taxAmount, buckets };
}

/**
 * 区分ごとに丸めた税額を、明細へ按分して戻す。
 *
 * 明細に税額を持たせるのは画面と PDF の都合だが、**単純に明細ごとへ
 * `amount * rate` を切り捨てて入れると、合計が `invoices.tax_amount` と
 * 1 円ずれる**。ずれた請求書は「小計 + 消費税 ≠ 合計」に見えるので、
 * 端数は必ずその区分の最後の明細に寄せて、合計が一致することを保証する。
 *
 * 戻り値は入力と同じ長さ・同じ順序。
 */
export function apportionTax(lines: readonly TaxableLine[]): number[] {
  const buckets = taxBuckets(lines);
  const target = new Map(buckets.map((b) => [`${b.taxCode}:${b.taxRateBp}`, b.taxAmount]));
  const lastIndex = new Map<string, number>();
  lines.forEach((line, index) => lastIndex.set(`${line.taxCode}:${line.taxRateBp}`, index));

  const assigned = new Map<string, number>();
  const out = lines.map((line, index) => {
    const key = `${line.taxCode}:${line.taxRateBp}`;
    if (lastIndex.get(key) === index) {
      // 区分の最後の明細に端数を寄せる。
      return (target.get(key) ?? 0) - (assigned.get(key) ?? 0);
    }
    const value = Math.trunc((line.amount * line.taxRateBp) / 10000);
    assigned.set(key, (assigned.get(key) ?? 0) + value);
    return value;
  });
  return out;
}

/* ------------------------------------------------------------ 按分 */

export interface ProrateInput {
  /** 期間の全日数（1 以上）。 */
  readonly periodDays: number;
  /** 課金する日数（0 以上 periodDays 以下）。 */
  readonly usedDays: number;
  /** 期間まるごとの金額（税抜、最小通貨単位）。 */
  readonly fullAmount: number;
}

/**
 * 日割り。**切り捨て**（契約者に有利な方向）で丸める。
 *
 * 切り上げにすると「1 日だけ使って解約した月に満額に近い額を請求された」という
 * 苦情の温床になる。返金でも同じ関数を使うので、丸め方向を 1 つに固定しておく。
 * 期間の全日を使ったときは按分せず満額を返す（除算の誤差で 1 円欠けないように）。
 */
export function prorate(input: ProrateInput): number {
  const { periodDays, usedDays, fullAmount } = input;
  if (!Number.isInteger(periodDays) || periodDays < 1) {
    throw new Error(`按分の期間日数が不正です: ${periodDays}`);
  }
  if (!Number.isInteger(usedDays) || usedDays < 0 || usedDays > periodDays) {
    throw new Error(`按分の使用日数が不正です: ${usedDays}（期間 ${periodDays} 日）`);
  }
  if (!Number.isSafeInteger(fullAmount)) {
    throw new Error(`按分の金額が整数ではありません: ${fullAmount}`);
  }
  if (usedDays === periodDays) return fullAmount;
  if (usedDays === 0) return 0;
  // 0 方向への切り捨て。負の金額（返金）でも「返す側が損をしない」向きに揃える。
  return Math.trunc((fullAmount * usedDays) / periodDays);
}

/* ------------------------------------------------- 日付（按分のための純関数） */

/**
 * `YYYY-MM-DD` を UTC のミリ秒に直す。
 *
 * 按分の日数計算は**暦日の引き算**であって時刻の引き算ではない。
 * タイムゾーン付きの `Date` を経由すると DST や UTC オフセットで 1 日ずれるので、
 * ここでは日付文字列を UTC 正午として扱わず、そのまま `Date.UTC` に載せる
 * （両辺とも同じ規則で作るので差分は必ず 86,400,000 の倍数になる）。
 */
function dayMs(dateString: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString);
  if (!m) throw new Error(`日付は YYYY-MM-DD 形式で指定してください: ${dateString}`);
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function msDay(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/** `from` から `to` までの**両端を含む**日数（同日なら 1）。逆順なら 0。 */
export function inclusiveDays(from: string, to: string): number {
  const days = Math.round((dayMs(to) - dayMs(from)) / 86_400_000) + 1;
  return days > 0 ? days : 0;
}

/** `YYYY-MM-DD` に日数を足す。 */
export function addCalendarDays(dateString: string, days: number): string {
  return msDay(dayMs(dateString) + days * 86_400_000);
}

/** その日が属する暦月の初日・末日と日数。 */
export function calendarMonthOf(dateString: string): {
  monthStart: string;
  monthEnd: string;
  monthDays: number;
} {
  const ms = dayMs(dateString);
  const d = new Date(ms);
  const year = d.getUTCFullYear();
  const month = d.getUTCMonth();
  const startMs = Date.UTC(year, month, 1);
  const endMs = Date.UTC(year, month + 1, 1) - 86_400_000;
  return {
    monthStart: msDay(startMs),
    monthEnd: msDay(endMs),
    monthDays: Math.round((endMs - startMs) / 86_400_000) + 1,
  };
}

/** 暦月の範囲を切り出した 1 区画。`from`〜`to` は両端を含む。 */
export interface MonthWindow {
  readonly monthStart: string;
  readonly monthEnd: string;
  /** 按分の**分母**。その暦月の日数（28〜31）。 */
  readonly monthDays: number;
  readonly from: string;
  readonly to: string;
}

/**
 * 日付範囲を**暦月ごと**に切り分ける。
 *
 * 基本料の前払い（REQUIREMENTS.md §8.3）は「翌月ぶんを先に請求する」ので、
 * 1 枚の請求書が 2 か月ぶんの基本料（移行月の追い付き + 翌月の前払い）を
 * 抱えることがある。**按分の分母は月ごとに違う**（2 月は 28 日、8 月は 31 日）ので、
 * 期間をまたいだまま 1 つの `periodDays` で割ると金額が狂う。
 * 分割をここ 1 か所に閉じ込め、`prorate()` の分母を月ごとに与える。
 *
 * 逆順（`to < from`）なら空配列。
 */
export function monthWindows(from: string, to: string): MonthWindow[] {
  if (dayMs(to) < dayMs(from)) return [];
  const out: MonthWindow[] = [];
  let cursor = from;
  // 月数は高々数十。上限を置いて、日付の不正で無限ループしないようにする。
  for (let guard = 0; guard < 600; guard += 1) {
    const month = calendarMonthOf(cursor);
    const windowTo = dayMs(month.monthEnd) < dayMs(to) ? month.monthEnd : to;
    out.push({ ...month, from: cursor, to: windowTo });
    if (windowTo === to || dayMs(windowTo) >= dayMs(to)) break;
    cursor = addCalendarDays(windowTo, 1);
  }
  return out;
}

/* ---------------------------------------------- 期中の在籍・プラン変更の分解 */

export interface BillingSegmentInput {
  /** 請求期間の初日（`YYYY-MM-DD`、含む）。 */
  readonly periodStart: string;
  /** 請求期間の末日（`YYYY-MM-DD`、含む）。 */
  readonly periodEnd: string;
  /** 役務提供の開始日。期間より前から在籍しているなら null。 */
  readonly serviceStart: string | null;
  /** 最終在籍日（含む）。期間末まで在籍しているなら null。 */
  readonly serviceEnd: string | null;
  /** 期間開始時点のプランコード。 */
  readonly planCode: string;
  /** 期間中のプラン変更（効力発生日はその日から新プラン）。 */
  readonly changes: readonly { readonly effectiveDate: string; readonly toPlanCode: string }[];
}

export interface BillingSegment {
  readonly planCode: string;
  /** 含む。 */
  readonly fromDate: string;
  /** 含む。 */
  readonly toDate: string;
  /** `fromDate`〜`toDate` の日数（両端を含む）。 */
  readonly days: number;
}

/**
 * 請求期間を「同じプランで在籍していた連続区間」に分解する。
 *
 * これが無いと、月末に契約した顧客へ満額を請求し、月中のダウングレードでは
 * 上位プランの分を取り損ねる。LP・特商法表記が「日割りで按分」と掲示している
 * 以上、**掲示と実装を一致させるための最小の道具**がこの関数になる。
 *
 * 期間まるごと 1 プランで在籍していた通常契約では、戻り値は 1 区間で
 * `days === 期間日数` になる。`prorate()` はその場合に満額を返すので、
 * 既存の請求金額は 1 円も変わらない（回帰しないことを test で固定してある）。
 */
export function buildBillingSegments(input: BillingSegmentInput): BillingSegment[] {
  const periodStartMs = dayMs(input.periodStart);
  const periodEndMs = dayMs(input.periodEnd);
  if (periodEndMs < periodStartMs) {
    throw new Error(`請求期間が逆転しています: ${input.periodStart} 〜 ${input.periodEnd}`);
  }

  const fromMs = Math.max(periodStartMs, input.serviceStart ? dayMs(input.serviceStart) : periodStartMs);
  const toMs = Math.min(periodEndMs, input.serviceEnd ? dayMs(input.serviceEnd) : periodEndMs);
  if (toMs < fromMs) return [];

  // 効力発生日順。同日に複数あるときは配列の順（＝ DB の並び）で後勝ちにする。
  const changes = [...input.changes]
    .map((c) => ({ ms: dayMs(c.effectiveDate), toPlanCode: c.toPlanCode }))
    .sort((a, b) => a.ms - b.ms);

  // 区間開始より前（または当日）に効力が生じた変更は、開始時点のプランへ畳む。
  let planCode = input.planCode;
  for (const change of changes) {
    if (change.ms <= fromMs) planCode = change.toPlanCode;
  }

  const segments: BillingSegment[] = [];
  let cursorMs = fromMs;
  for (const change of changes) {
    if (change.ms <= fromMs || change.ms > toMs) continue;
    if (change.toPlanCode === planCode) continue;
    const segEnd = change.ms - 86_400_000;
    if (segEnd >= cursorMs) {
      segments.push({
        planCode,
        fromDate: msDay(cursorMs),
        toDate: msDay(segEnd),
        days: Math.round((segEnd - cursorMs) / 86_400_000) + 1,
      });
    }
    cursorMs = change.ms;
    planCode = change.toPlanCode;
  }
  segments.push({
    planCode,
    fromDate: msDay(cursorMs),
    toDate: msDay(toMs),
    days: Math.round((toMs - cursorMs) / 86_400_000) + 1,
  });
  return segments;
}

/* ------------------------------------------------------------ 年払いの請求月 */

/**
 * 年払い契約を、その `yyyyMM` に請求すべきか。
 *
 * **UTC の月で比べてはいけない。** `current_period_start` は
 * timestamptz なので、JST の 9 月 1 日 00:00 は UTC では 8 月 31 日 15:00 に
 * なる。`Date#getUTCMonth()` で比べると年払い契約が毎年 1 か月ずれ、
 * 時刻の巡り合わせによっては 1 年まったく請求が立たない。
 *
 * 引数はどちらも**契約者タイムゾーンで整形済みの `yyyyMM`**（文字列）にして、
 * 呼び出し側に変換を強制する。
 */
export function isYearlyBillingMonth(anniversaryYearMonth: string, targetYearMonth: string): boolean {
  for (const value of [anniversaryYearMonth, targetYearMonth]) {
    if (!/^\d{6}$/.test(value)) {
      throw new Error(`年月は yyyyMM 形式で指定してください: ${value}`);
    }
  }
  return anniversaryYearMonth.slice(4, 6) === targetYearMonth.slice(4, 6);
}

/* ------------------------------------------------------------ 総額表示 */

/**
 * 税抜金額から税込金額を出す（切り捨て）。
 *
 * カタログ（`PLAN_CATALOG`）と `plans.monthly_price` は**税抜**が正。
 * 一方、消費者向けの価格表示には消費税法 63 条の総額表示義務があるので、
 * 画面・LP・特商法表記は必ず税込を主に出す。**その変換をここ 1 か所に集める**。
 */
/**
 * **掲示・画面で使う既定の消費税率**（basis point。10% = 1000）。
 *
 * 請求書の税率の正は `tax_rates` 表（`effective_from` を持つ履歴表）で、
 * 発行済みの請求書には `invoices.tax_rate_bp` として焼き付ける。
 * ここに定数を置くのは「まだ請求書になっていない金額」（料金ページ・
 * 特商法表記・契約者画面の見込み額）の総額表示に使うためで、
 * **税率が変わったら `TAX_RATE_SEED` とこの定数を同時に直す**。
 */
export const DEFAULT_TAX_RATE_BP = 1000;

export function taxIncludedAmount(amountExclTax: number, taxRateBp: number): number {
  if (!Number.isSafeInteger(amountExclTax)) {
    throw new Error(`税込計算に整数以外の金額が渡されました: ${amountExclTax}`);
  }
  return amountExclTax + Math.trunc((amountExclTax * taxRateBp) / 10000);
}

/**
 * 総額表示の文字列。`1,650 円（税込・税抜 1,500 円）`。
 *
 * 表記のゆれ（「税込」だけ / 「税抜」だけ）を各画面が自前で組み立てると、
 * 同じ金額が別々の意味で並ぶ。文言ごとここで固定する。
 */
export function formatJpyWithTax(amountExclTax: number, taxRateBp: number): string {
  const incl = taxIncludedAmount(amountExclTax, taxRateBp);
  return `${incl.toLocaleString('ja-JP')} 円（税込・税抜 ${amountExclTax.toLocaleString('ja-JP')} 円）`;
}

/**
 * 期中のプラン変更の差額。
 *
 * 旧プランの未使用日数分を戻し、新プランの残日数分を課金する。
 * **戻しと課金を別々に丸める**のが要点。合算してから丸めると、
 * 「差額 0 円のはずが 1 円請求される」ケースが出る。
 */
export function planChangeDelta(input: {
  readonly periodDays: number;
  /** 変更後に残る日数（変更日当日を含む）。 */
  readonly remainingDays: number;
  readonly fromAmount: number;
  readonly toAmount: number;
}): { refund: number; charge: number; delta: number } {
  const refund = prorate({
    periodDays: input.periodDays,
    usedDays: input.remainingDays,
    fullAmount: input.fromAmount,
  });
  const charge = prorate({
    periodDays: input.periodDays,
    usedDays: input.remainingDays,
    fullAmount: input.toAmount,
  });
  return { refund, charge, delta: charge - refund };
}

/* ------------------------------------------------------------ SLA */

/** SLA の階梯。稼働率がしきい値を下回ったら、その段のクレジット率を適用する。 */
export interface SlaTier {
  /** この値**未満**なら該当（basis point。99.9% = 9990）。 */
  readonly belowBp: number;
  /** 月額に対する返金率（basis point。10% = 1000）。 */
  readonly creditBp: number;
}

/**
 * 掲示している月間稼働率の保証値（basis point）。99.5% = 9950。
 *
 * **`/legal/sla` の「目標値」表と同じ定数から出す。** 以前は掲示（99.5%）と
 * コード（最初の境界が 99.90%）が別々に書かれていて、しかも階梯の中身も
 * 食い違っていた（掲示に無い 20% の段があり、掲示した 100% の段が無かった）。
 * 掲示は債務なので、**コードが返せない救済を掲示している**状態はそれ自体が事故。
 */
export const SLA_UPTIME_GUARANTEE_BP = 9950;

/**
 * 保証値からサービスクレジットの階梯を作る。
 *
 * 段は掲示（`/legal/sla` 第 5 条）と 1 対 1:
 *   - 保証値未満（99.5% 未満 99.0% 以上）… 10%
 *   - 99.0% 未満 95.0% 以上 … 30%
 *   - 95.0% 未満 … 100%
 *
 * `slaCreditBp()` は `Math.max` で最も重い段を採るので、境界の重なりは
 * 「下の段ほど重い」形で自然に解決される（順序は不問）。
 *
 * **保証値が 0 のプランは空配列を返す。** SLA を約束していないプランに
 * クレジットを自動起票しないための入口がここ（R21）。上位プランが
 * 99.95% を約束する場合は、その値を渡せば最初の段が自動でそこへ動く。
 */
export function slaTiersFor(guaranteeBp: number): readonly SlaTier[] {
  if (!Number.isFinite(guaranteeBp) || guaranteeBp <= 0) return [];
  const tiers: SlaTier[] = [{ belowBp: Math.round(guaranteeBp), creditBp: 1000 }];
  // 保証値が下の段より低いプランでは、その段は意味を持たない（重複させない）。
  if (guaranteeBp > 9900) tiers.push({ belowBp: 9900, creditBp: 3000 });
  if (guaranteeBp > 9500) tiers.push({ belowBp: 9500, creditBp: 10000 });
  return tiers;
}

/**
 * 既定の SLA 階梯（`/legal/sla` に掲示する値と一致させること）。
 * **掲示だけして履行しない状態を作らない**ために、コード側にも同じ表を置く。
 *
 * 掲示側（`legal.ts` の `slaDocument`）もこの関数から表を作るので、
 * 片方だけ直すことができない。`billing-math.test.ts` が突合を固定している。
 */
export const DEFAULT_SLA_TIERS: readonly SlaTier[] = slaTiersFor(SLA_UPTIME_GUARANTEE_BP);

/**
 * 計画メンテナンスの事前告知日数と、SLA 対象外にできる月間の上限（分）。
 *
 * **上限を掲示せずに「計画メンテナンスは停止分数に含めない」とだけ書くと、
 * いくらでも免責を主張できる書きぶりになる**（消費者契約法 10 条のリスク）。
 * 掲示・入力検査・稼働率の算定の 3 か所が同じ定数を読む。
 */
export const PLANNED_MAINTENANCE_NOTICE_DAYS = 7;
export const PLANNED_MAINTENANCE_MONTHLY_MINUTES_MAX = 240;

/**
 * SLA クレジットの申告期限（対象月の翌月末日 23:59:59.999、契約者 tz）。
 *
 * 掲示（`/legal/sla`「対象月の翌月末日までに」）と同じ値をコードから引くための
 * 純関数。`yyyyMM`（区切りなし）で受ける（`lib/time.ts` と同じ表記）。
 */
export function slaClaimDeadline(yearMonth: string, utcOffsetMinutes = 540): Date {
  if (!/^\d{6}$/.test(yearMonth)) {
    throw new Error(`SLA 申告期限の対象月が yyyyMM ではありません: ${yearMonth}`);
  }
  const year = Number(yearMonth.slice(0, 4));
  const month = Number(yearMonth.slice(4, 6));
  // 対象月の 2 か月後の 1 日 00:00（＝翌月末日の終わり）から 1 ミリ秒引く。
  const endUtc = Date.UTC(year, month + 1, 1) - utcOffsetMinutes * 60_000;
  return new Date(endUtc - 1);
}

/** 申告期限を過ぎているか。 */
export function isSlaClaimExpired(
  yearMonth: string,
  now: Date,
  utcOffsetMinutes = 540,
): boolean {
  return now.getTime() > slaClaimDeadline(yearMonth, utcOffsetMinutes).getTime();
}

/**
 * 稼働率（basis point）。分母は「期待するハートビート数」。
 *
 * 分母が 0（ノードが 1 台も無い / 期間が 0）のときは **100% ではなく null** を返す。
 * 計測できていないことを 100% と読み替えると、実際には止まっていた月に
 * クレジットが出ない。
 */
export function uptimeBp(observed: number, expected: number): number | null {
  if (!Number.isFinite(observed) || !Number.isFinite(expected)) return null;
  if (expected <= 0) return null;
  const capped = Math.min(observed, expected);
  return Math.max(0, Math.round((capped / expected) * 10000));
}

/**
 * SLA 対象外の時間を分母から除いた稼働率。
 *
 * 掲示（`/legal/sla` 第 3 条）は「事前告知した計画メンテナンス・不可抗力・
 * 契約者起因・AUP 違反による停止措置は停止分数に含めない」と書いている。
 * その除外を実装しないと、**告知したメンテナンスを行うたびに自分で
 * SLA クレジットを起票する**ことになる。
 *
 * `excludedExpected` は「除外区間に本来届くはずだった観測数」。分母から
 * 引き、分子は除外区間の観測が既に落ちている前提で分母に丸める
 * （メンテナンス中もハートビートが届いていた場合に 100% を超えないため）。
 */
export function uptimeBpExcluding(
  observed: number,
  expected: number,
  excludedExpected: number,
): number | null {
  if (!Number.isFinite(excludedExpected) || excludedExpected < 0) return null;
  const denominator = expected - excludedExpected;
  // 月まるごとが対象外なら「判断しない」。0 除算を 100% と読み替えない。
  if (denominator <= 0) return null;
  return uptimeBp(observed, denominator);
}

/** 稼働率から返金率（basis point）を決める。該当しなければ 0。 */
export function slaCreditBp(
  uptime: number,
  tiers: readonly SlaTier[] = DEFAULT_SLA_TIERS,
): number {
  let credit = 0;
  for (const tier of tiers) {
    if (uptime < tier.belowBp) credit = Math.max(credit, tier.creditBp);
  }
  return credit;
}

/** 返金額（税抜、切り捨て）。基準額は「その月の基本料金」。 */
export function slaCreditAmount(monthlyAmount: number, creditBp: number): number {
  if (!Number.isSafeInteger(monthlyAmount)) {
    throw new Error(`SLA クレジットの基準額が整数ではありません: ${monthlyAmount}`);
  }
  return Math.trunc((monthlyAmount * creditBp) / 10000);
}

/* -------------------------------------------------- ゼロ小数通貨（決済） */

/**
 * ゼロ小数通貨（最小単位が 1 の通貨）。
 *
 * **決済事業者へ金額を渡す入口で必ず検査する**（REQUIREMENTS.md §8.3）。
 * Stripe の金額は「最小通貨単位の整数」で、USD なら 1500 = $15.00 だが
 * JPY は 1500 = 1,500 円。**JPY を 100 倍して渡すのが典型的な事故**で、
 * 100 倍の請求が実際に顧客のカードへ通ってしまう。この関数を
 * アダプタの `charge()` / `refund()` の先頭で呼ぶことで、
 * 「100 倍する実装」を書いた人が実行時に必ず気付くようにする。
 *
 * ここに載っているのは ISO 4217 で小数部を持たない通貨のうち、
 * 本サービスが扱いうるもの。**扱わない通貨は「対応していない」ことを
 * 明示的に throw する**（黙って 100 倍しない）。
 */
export const ZERO_DECIMAL_CURRENCIES = ['JPY', 'KRW', 'VND', 'CLP', 'ISK'] as const;

export function isZeroDecimalCurrency(currency: string): boolean {
  return (ZERO_DECIMAL_CURRENCIES as readonly string[]).includes(currency.toUpperCase());
}

/**
 * 決済事業者へ渡す金額の検査。
 *
 *   - 通貨がゼロ小数通貨であること（他の通貨は最小単位への換算が要るが未実装）
 *   - 金額が非負の安全な整数であること
 *
 * 通れば「その値をそのまま渡してよい」ことが保証される。
 */
export function assertZeroDecimal(currency: string, amount: number): void {
  if (!isZeroDecimalCurrency(currency)) {
    throw new Error(
      `${currency} はゼロ小数通貨ではありません。最小単位への換算が未実装のため決済に渡せません`,
    );
  }
  if (!Number.isSafeInteger(amount)) {
    throw new Error(`決済金額が整数ではありません: ${amount}`);
  }
  if (amount < 0) {
    throw new Error(`決済金額が負です: ${amount}`);
  }
}

/* ------------------------------------------ 請求書の明細（プレビューと共通） */

/**
 * 請求書明細の種別。`invoice_line_kind` enum と 1 対 1。
 *
 * **`overage_outbound` に生成経路を作らない**（REQUIREMENTS.md §8.2）。
 * 型に残してあるのは、過去に発行済みの請求書を表示するため。
 */
export type InvoiceLineKind =
  | 'plan'
  | 'overage_mailbox'
  | 'overage_storage'
  | 'overage_outbound'
  /**
   * Webメールの AI 利用（入力 + 出力トークン）の超過（0061）。
   *
   * **上限に達したときの挙動を契約者が `onLimit='meter'` にしているときだけ**
   * 生成する。`block` の契約は上限で止まる約束なので、漏れて記録された
   * トークンを後から請求すると「止める設定にしていたのに請求された」になる。
   */
  | 'overage_ai_tokens'
  | 'addon'
  | 'discount'
  | 'sla_credit'
  | 'adjustment';

export interface InvoiceDraftLine {
  readonly kind: InvoiceLineKind;
  readonly description: string;
  readonly quantityMilli: number;
  readonly unitPrice: number;
  readonly amount: number;
  /** 省略時は標準税率。 */
  readonly taxCode?: TaxCode;
  readonly metadata: Record<string, unknown>;
}

/** プランの表示名と単価（契約に焼き付いた価格があればそちら）。 */
export type PlanPriceLookup = (planCode: string) => { name: string; unitPrice: number };

/**
 * 基本料を立てるべき暦月の区画（**前払い**）。
 *
 * 対象月 `periodStart`〜`periodEnd` を締めるとき、基本料は
 * 「前払い済みの翌日 〜 **翌月末**」を立てる。`paidThrough` が null
 * （前払いへ切り替える前の契約）なら対象月の初日から始まるので、
 * その 1 枚だけ当月（後払いの追い付き）と翌月（前払い）の 2 か月分になる。
 *
 * **プレビュー（運営コンソール）と本発行（worker）が必ず同じ区画を使う**ように、
 * 区画の決め方をここ 1 か所に置く。片方だけ変えると、運営が見た金額と
 * 実際の請求額が食い違う（A243 はまさにそれだった）。
 */
export function baseFeeWindows(input: {
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly paidThrough: string | null;
}): { windows: MonthWindow[]; baseTo: string; transitional: boolean } {
  const baseTo = calendarMonthOf(addCalendarDays(input.periodEnd, 1)).monthEnd;
  const baseFrom = input.paidThrough
    ? addCalendarDays(input.paidThrough, 1)
    : input.periodStart;
  return {
    windows: monthWindows(baseFrom, baseTo),
    baseTo,
    transitional: !input.paidThrough,
  };
}

export interface BaseFeeInput {
  /** 課金する暦月の区画（`monthWindows()` の戻り値）。 */
  readonly windows: readonly MonthWindow[];
  /** 役務提供の開始日。区画より前から在籍しているなら null。 */
  readonly serviceStart: string | null;
  /** 最終在籍日（含む）。継続中なら null。 */
  readonly serviceEnd: string | null;
  /** 区画の頭の時点で有効なプラン。 */
  readonly planCode: string;
  readonly changes: readonly { readonly effectiveDate: string; readonly toPlanCode: string }[];
  readonly quantity: number;
  readonly priceOf: PlanPriceLookup;
  /** その区画を「前払い」と表示するか（後払いの追い付き分は false）。既定は true。 */
  readonly isAdvance?: (window: MonthWindow) => boolean;
}

/**
 * **同じプランの隣り合う区間をつなぐ。**
 *
 * `buildBillingSegments()` は「その日に効力が生じた変更」ごとに区間を切るので、
 * 同じ日に `standard → business → standard` と往復した（＝結果として何も
 * 変わっていない）操作でも区間が 2 本に割れる。割れたまま按分すると
 * **区間ごとに切り捨てが起き、満額より 1 円少ない**請求書になる。
 * 金額そのものは契約者に有利な向きだが、プレビューと本発行で区間の数が
 * 変わると金額も変わるので、**式を共有した意味が消える**。
 */
function mergeAdjacentSegments(segments: readonly BillingSegment[]): BillingSegment[] {
  const out: BillingSegment[] = [];
  for (const segment of segments) {
    const last = out[out.length - 1];
    if (last && last.planCode === segment.planCode) {
      out[out.length - 1] = {
        planCode: last.planCode,
        fromDate: last.fromDate,
        toDate: segment.toDate,
        days: last.days + segment.days,
      };
      continue;
    }
    out.push(segment);
  }
  return out;
}

/**
 * 基本料の明細を作る（**前払い**・暦月ごとに按分）。
 *
 * REQUIREMENTS.md §8.3 は「月額基本料は前払い、超過分は後払い」。
 * 呼び出し側が「まだ基本料を請求していない期間」を `windows` として渡し、
 * ここは金額だけを決める。**プレビュー（運営コンソール）と本発行（worker）が
 * 同じ関数を通る**ようにするのが目的で、以前は 2 か所に別々の式があり、
 * 運営が見る売上見込みと実際の発行額が食い違っていた（A243）。
 *
 * 按分の分母は**その暦月の日数**。期間をまたいだまま 1 つの分母で割ると、
 * 2 月と 8 月が同じ日割り単価になってしまう。
 */
export function buildBaseFeeLines(input: BaseFeeInput): InvoiceDraftLine[] {
  const quantity = Math.max(1, Math.trunc(input.quantity || 1));
  const lines: InvoiceDraftLine[] = [];
  for (const window of input.windows) {
    const segments = mergeAdjacentSegments(
      buildBillingSegments({
        periodStart: window.from,
        periodEnd: window.to,
        serviceStart: input.serviceStart,
        serviceEnd: input.serviceEnd,
        planCode: input.planCode,
        changes: input.changes,
      }),
    );
    for (const segment of segments) {
      const price = input.priceOf(segment.planCode);
      const fullAmount = price.unitPrice * quantity;
      const amount = prorate({
        periodDays: window.monthDays,
        usedDays: segment.days,
        fullAmount,
      });
      const prorated = segment.days < window.monthDays;
      const advance = input.isAdvance ? input.isAdvance(window) : true;
      const suffix = [
        prorated ? `${segment.days}/${window.monthDays} 日の日割り` : null,
        advance ? '前払い' : null,
      ]
        .filter((s): s is string => s !== null)
        .join('・');
      lines.push({
        kind: 'plan',
        description: `${price.name}（${segment.fromDate} 〜 ${segment.toDate}${suffix ? `・${suffix}` : ''}）`,
        quantityMilli: quantity * 1000,
        unitPrice: price.unitPrice,
        amount,
        metadata: {
          planCode: segment.planCode,
          basePeriodStart: segment.fromDate,
          basePeriodEnd: segment.toDate,
          advance,
          prorated: amount !== fullAmount,
          periodDays: window.monthDays,
          usedDays: segment.days,
          fullAmount,
        },
      });
    }
  }
  return lines;
}

/**
 * 前払い済みで役務提供が無くなった期間の返金明細（負の `adjustment`）。
 *
 * 解約すると `base_fee_paid_through` より前に役務が終わる。**前払いしたまま
 * 返さない**のは、特商法表記に書いた「日割りで精算」と食い違う。
 * 返金も `prorate()`（切り捨て）を通し、丸めの向きを課金と揃える。
 */
export function buildPrepaidRefundLines(input: {
  readonly windows: readonly MonthWindow[];
  readonly planCode: string;
  readonly quantity: number;
  readonly priceOf: PlanPriceLookup;
}): InvoiceDraftLine[] {
  const quantity = Math.max(1, Math.trunc(input.quantity || 1));
  const lines: InvoiceDraftLine[] = [];
  for (const window of input.windows) {
    const days = inclusiveDays(window.from, window.to);
    if (days <= 0) continue;
    const price = input.priceOf(input.planCode);
    const amount = prorate({
      periodDays: window.monthDays,
      usedDays: days,
      fullAmount: price.unitPrice * quantity,
    });
    if (amount === 0) continue;
    lines.push({
      kind: 'adjustment',
      description: `${price.name} 前払い分の返金（${window.from} 〜 ${window.to}・${days}/${window.monthDays} 日）`,
      quantityMilli: quantity * 1000,
      unitPrice: -price.unitPrice,
      amount: -amount,
      metadata: {
        planCode: input.planCode,
        refundFrom: window.from,
        refundTo: window.to,
        periodDays: window.monthDays,
        usedDays: days,
      },
    });
  }
  return lines;
}

const GIB = 1024 ** 3;

export interface OverageInput {
  readonly quantity: number;
  /** 契約に含まれるメールボックス数（0 = 無制限）。 */
  readonly mailboxesMax: number;
  readonly perMailboxCents: number;
  readonly peakMailboxes: number;
  /** 契約に含まれる容量（バイト、0 = 無制限）。 */
  readonly storageBytesTotal: number;
  readonly perGiBCents: number;
  readonly peakStorageBytes: number;
  /**
   * AI の無料枠（入力 + 出力トークンの合計、**0 = 無制限＝課金しない**）。
   *
   * 渡すのは `resolveAiPolicy()` が合成した**実効上限**（プランの上限と
   * 契約者が自衛で下げた値の小さいほう）。プランの生値を渡すと、
   * 契約者が上限を下げて止めているのに超過課金だけが走る。
   * **`quantity` を掛けない。** 上限を判定して実際に止める側
   * （`ai-usage.ts` の `enforceMonthlyCap`）が組織単位の 1 本で見ているので、
   * ここで席数を掛けると「止まったのに無料枠が余っている」請求書になる。
   */
  readonly aiFreeTokens?: number;
  /** 100 万トークンあたりの単価（税抜・最小通貨単位）。0 なら課金しない。 */
  readonly perMillionAiTokensCents?: number;
  /** 対象月に実際に使ったトークン（入力 + 出力）。 */
  readonly aiTokensUsed?: number;
  /**
   * 超過ぶんを課金してよいか（契約者の `onLimit='meter'`）。
   * 既定 false ＝**黙って課金しない**。
   */
  readonly aiMetered?: boolean;
}

/** AI の従量課金の単位（100 万トークン）。掲示・請求・画面が同じ分母を使う。 */
export const AI_TOKEN_BILLING_UNIT = 1_000_000;

/**
 * 超過（従量）の明細。**後払い**（対象月の実績）。
 *
 * `0 = 無制限` の規約は `exceedsLimit()` と同じ。単価 0 のときも明細を作らない
 * （0 円の行が並ぶ請求書は、契約者に「何を請求されたのか」を説明できない）。
 */
export function buildOverageLines(input: OverageInput): InvoiceDraftLine[] {
  const quantity = Math.max(1, Math.trunc(input.quantity || 1));
  const lines: InvoiceDraftLine[] = [];

  if (input.mailboxesMax > 0 && input.perMailboxCents > 0) {
    const included = input.mailboxesMax * quantity;
    const excess = Math.max(0, input.peakMailboxes - included);
    if (excess > 0) {
      lines.push({
        kind: 'overage_mailbox',
        description: `メールボックス超過（${excess} 件）`,
        quantityMilli: excess * 1000,
        unitPrice: input.perMailboxCents,
        amount: excess * input.perMailboxCents,
        metadata: { peak: input.peakMailboxes, included },
      });
    }
  }

  if (input.storageBytesTotal > 0 && input.perGiBCents > 0) {
    const includedBytes = input.storageBytesTotal * quantity;
    const excessBytes = Math.max(0, input.peakStorageBytes - includedBytes);
    // 1 GiB 未満は切り上げ。端数を無料にすると計測誤差で毎月ぶれる。
    const excessGiB = Math.ceil(excessBytes / GIB);
    if (excessGiB > 0) {
      lines.push({
        kind: 'overage_storage',
        description: `容量超過（${excessGiB} GiB）`,
        quantityMilli: excessGiB * 1000,
        unitPrice: input.perGiBCents,
        amount: excessGiB * input.perGiBCents,
        metadata: { peakBytes: input.peakStorageBytes, includedBytes },
      });
    }
  }

  /*
   * --- AI トークンの超過（0061） ---
   *
   * 単価（`perMillionAiTokensCents`）とカタログ側の掲示は前からあったのに
   * **消費する側が 1 か所も無かった**。それでいて上限超過の通知は
   * 「超過ぶんは今月の請求に加算されます」と契約者へ送っていたので、
   * 掲示・通知・請求書の 3 つが食い違っていた。
   *
   * 課金するのは 3 条件が揃ったときだけ:
   *   1. 無料枠がある（0 = 無制限なら「超過」という概念が無い）
   *   2. 単価がある（0 のプランは無料で回す判断）
   *   3. 契約者が `onLimit='meter'`（止めずに従量課金で継続）を選んでいる
   *
   * 数量は 100 万トークン単位の**小数**。1 トークン単位で四捨五入すると
   * 単価 800 円 / 100 万トークンでは 1 トークンあたり 0.0008 円になり、
   * 整数の単価では表現できない。`quantity_milli`（1/1000 単位）に
   * 100 万トークン単位の数量を入れ、金額は「超過トークン × 単価 ÷ 100 万」を
   * **切り捨て**（`Math.trunc`）て出す。丸めの向きは他の明細と揃える。
   */
  const freeTokens = input.aiFreeTokens ?? 0;
  const perMillion = input.perMillionAiTokensCents ?? 0;
  const usedTokens = input.aiTokensUsed ?? 0;
  if (input.aiMetered === true && freeTokens > 0 && perMillion > 0) {
    const excessTokens = Math.max(0, Math.trunc(usedTokens) - freeTokens);
    const amount = Math.trunc((excessTokens * perMillion) / AI_TOKEN_BILLING_UNIT);
    // 単価未満の超過（例: 1,000 トークン）は 0 円になる。0 円の行は出さない。
    if (amount > 0) {
      lines.push({
        kind: 'overage_ai_tokens',
        description: `AI トークン超過（${excessTokens.toLocaleString('ja-JP')} トークン）`,
        quantityMilli: Math.round((excessTokens / AI_TOKEN_BILLING_UNIT) * 1000),
        unitPrice: perMillion,
        amount,
        metadata: {
          usedTokens,
          freeTokens,
          excessTokens,
          unit: AI_TOKEN_BILLING_UNIT,
        },
      });
    }
  }

  return lines;
}

export interface PendingSlaCredit {
  readonly id: string;
  /** 未適用の残額（正の数）。 */
  readonly amount: number;
  readonly reason: string;
}

export interface AppliedSlaCredit {
  readonly id: string;
  /** 今回の請求書へ充当した額（正の数）。 */
  readonly applied: number;
  /** 次回以降へ繰り越す残額（正の数、0 なら完了）。 */
  readonly remaining: number;
}

/**
 * SLA クレジットを**課税対象小計の範囲内に切り詰めて**充当する（A245）。
 *
 * クレジットが基本料を上回ると請求書の合計が負になるが、**負の請求書を
 * 精算する経路が無い**ので、そのまま出すとクレジットは `applied` として
 * 消え、契約者は受け取れないまま終わる。合計が 0 になる請求書は出してよいが
 * 負にはせず、**充当しきれなかった分は残額として次回へ繰り越す**。
 *
 * `subtotalBeforeCredits` は基本料・超過・調整までの小計（税抜）。
 */
export function applySlaCredits(
  credits: readonly PendingSlaCredit[],
  subtotalBeforeCredits: number,
): { lines: InvoiceDraftLine[]; applied: AppliedSlaCredit[] } {
  let room = Math.max(0, subtotalBeforeCredits);
  const lines: InvoiceDraftLine[] = [];
  const applied: AppliedSlaCredit[] = [];
  for (const credit of credits) {
    const amount = Math.max(0, Math.trunc(credit.amount));
    const use = Math.min(room, amount);
    applied.push({ id: credit.id, applied: use, remaining: amount - use });
    if (use <= 0) continue;
    room -= use;
    const carried = amount - use;
    lines.push({
      kind: 'sla_credit',
      description:
        carried > 0
          ? `SLA クレジット: ${credit.reason}（今回充当 ${use.toLocaleString('ja-JP')} 円 / 残 ${carried.toLocaleString('ja-JP')} 円は翌月以降へ繰越）`
          : `SLA クレジット: ${credit.reason}`,
      quantityMilli: 1000,
      unitPrice: -use,
      amount: -use,
      metadata: { slaCreditId: credit.id, creditAmount: amount, applied: use, remaining: carried },
    });
  }
  return { lines, applied };
}

/** 明細の税抜合計。 */
export function sumLineAmounts(lines: readonly InvoiceDraftLine[]): number {
  return lines.reduce((sum, l) => sum + l.amount, 0);
}

/* ------------------------------------------------ 支払サイト（支払期限） */

/**
 * 支払サイト。**日本の法人取引の既定は「月末締め翌月末払い」**。
 *
 * 以前は全社一律で「発行日 + 30 日」だったので、経理が月末締めで動く
 * 契約者の稟議に必ず乗らず（＝毎月「期日までに払えない」請求書を出し）、
 * 督促 → 送信停止の自動化がその食い違いの上で走っていた。
 *
 * `prepaid` は前払い（＝発行と同時に期日到来）。カード決済の契約は
 * 実際には発行直後に引き落とすので、督促の起点をずらさないために持つ。
 */
export const PAYMENT_TERMS = [
  'prepaid',
  'net15',
  'net30',
  'net60',
  'eom_next_15',
  'eom_next_25',
  'eom_next_eom',
] as const;
export type PaymentTerms = (typeof PAYMENT_TERMS)[number];

/*
 * 表示名は **`labels.ja.ts` の `JA_LABELS` 側に一本化**した（`labelOf()` で引く）。
 * ここに専用マップを置くと、語彙検査（`labels.ja.test.ts`）の網から外れたまま
 * 2 か所に同じ文言が並び、片方だけ直す事故が起きる。実際この表は
 * 定義されただけで 1 か所からも参照されていなかった。
 */

/**
 * 支払期限の暦日（`yyyy-mm-dd`）。
 *
 * 締め日基準（`eom_*`）は**役務提供期間の末日が属する月**を締め月として数える。
 * 発行日基準にすると、月次締めが遅れた月だけ期日が 1 か月ずれる
 * （＝運営の都合で契約者の支払期日が動く）。
 *
 * 返すのは暦日だけ。時刻（その日の 23:59:59 か翌 0:00 か）は呼び出し側が
 * 契約者のタイムゾーンで決める。ここに `Date` を持ち込むと、tz の解釈が
 * 純関数の中に混ざって単体テストで固定できなくなる。
 */
export function invoiceDueDate(input: {
  /** 請求書の発行日（契約者 tz の暦日）。 */
  readonly issuedOn: string;
  /** 役務提供期間の末日（契約者 tz の暦日）。締め日基準の起点。 */
  readonly periodEnd: string;
  readonly terms: PaymentTerms;
}): string {
  const { issuedOn, periodEnd, terms } = input;
  switch (terms) {
    case 'prepaid':
      return issuedOn;
    case 'net15':
      return addCalendarDays(issuedOn, 15);
    case 'net30':
      return addCalendarDays(issuedOn, 30);
    case 'net60':
      return addCalendarDays(issuedOn, 60);
    default:
      break;
  }
  // 締め月の翌月。`addCalendarDays(monthEnd, 1)` で必ず翌月 1 日に着く。
  const nextMonth = calendarMonthOf(addCalendarDays(calendarMonthOf(periodEnd).monthEnd, 1));
  const day =
    terms === 'eom_next_15' ? 15 : terms === 'eom_next_25' ? 25 : 0;
  if (day === 0) return nextMonth.monthEnd;
  const candidate = `${nextMonth.monthStart.slice(0, 8)}${String(day).padStart(2, '0')}`;
  // 2 月のように月末が 25 日より前になることは無いが、暦の安全側で丸めておく。
  return dayMs(candidate) > dayMs(nextMonth.monthEnd) ? nextMonth.monthEnd : candidate;
}

/* --------------------------------- 前払い済み区間に効いたプラン変更の差額 */

/** 前払い済み区間に効力が生じた変更 1 件。 */
export interface PrepaidChange {
  /** 効力発生日（契約者 tz の暦日）。 */
  readonly effectiveDate: string;
  readonly fromPlanCode: string;
  readonly toPlanCode: string;
  /** 変更前の数量（席数）。 */
  readonly fromQuantity: number;
  readonly toQuantity: number;
}

/**
 * **前払い済みの期間に効力が生じたプラン変更・数量変更の差額**（0061）。
 *
 * 基本料は前払いなので、月 M の基本料は M-1 の請求書で徴収済み。ところが
 * プラン変更は**即時適用**（値上がり側）なので、月 M の途中で上位プランへ
 * 上げると、その月の残日数は上位プランの役務を受けているのに、請求は
 * 下位プランのまま終わっていた（`buildBaseFeeLines` の窓は M+1 以降しか
 * 見ないので、差額の生まれる場所がどこにも無かった）。
 *
 * ここは「変更日 〜 前払い済みの終端」を暦月ごとに切り、
 * `planChangeDelta()`（戻しと課金を別々に丸める）で差額を出す。
 * 正なら追加請求、負なら返金。**0 円の行は出さない。**
 *
 * 引数の `changes` は**効力発生日の昇順**で渡すこと。同じ月に 2 回変更した
 * ときは、区間が `[変更1 〜 変更2 の前日]` と `[変更2 〜 終端]` に割れる。
 */
export function buildPrepaidChangeAdjustmentLines(input: {
  /** 前払い済み区間の開始（通常は対象月の初日）。 */
  readonly prepaidFrom: string;
  /** 前払い済みの終端（`subscriptions.base_fee_paid_through`）。 */
  readonly prepaidThrough: string;
  readonly changes: readonly PrepaidChange[];
  readonly priceOf: PlanPriceLookup;
}): InvoiceDraftLine[] {
  const lines: InvoiceDraftLine[] = [];
  const changes = [...input.changes]
    .filter(
      (c) =>
        dayMs(c.effectiveDate) >= dayMs(input.prepaidFrom) &&
        dayMs(c.effectiveDate) <= dayMs(input.prepaidThrough),
    )
    .sort((a, b) => dayMs(a.effectiveDate) - dayMs(b.effectiveDate));

  /*
   * **差額は「支払い済みの状態」との差で出す。** 直前の区間との差にすると、
   * 同じ月に 2 回変えたときに戻しの基準がずれる。
   *
   *   前払い: A（月額満額）／ d1 で A→B ／ d2 で B→C
   *   正しい: (B−A)×d1 区間 + (C−A)×d2 区間
   *   誤り  : (B−A)×d1 区間 + (C−B)×d2 区間  ← A ぶんを 1 回しか戻していない
   *
   * 支払い済みの状態は「窓の中で最初に起きた変更の変更前」。窓より前の
   * 変更は呼び出し側が既に畳んでいる（`buildBillingSegments` と同じ規約）。
   */
  const paidPlanCode = changes[0]?.fromPlanCode ?? '';
  const paidQuantity = Math.max(1, Math.trunc(changes[0]?.fromQuantity || 1));

  for (const [index, change] of changes.entries()) {
    // 次の変更の前日まで（無ければ前払い済みの終端まで）が、この差額の効く区間。
    const next = changes[index + 1];
    const until = next ? addCalendarDays(next.effectiveDate, -1) : input.prepaidThrough;
    if (dayMs(until) < dayMs(change.effectiveDate)) continue;

    const fromPrice = input.priceOf(paidPlanCode);
    const toPrice = input.priceOf(change.toPlanCode);
    const fromQuantity = paidQuantity;
    const toQuantity = Math.max(1, Math.trunc(change.toQuantity || 1));

    // **暦月ごとに按分する。** 分母（月の日数）が月で違うので、
    // 期間をまたいだまま 1 つの分母で割ると金額が狂う（`monthWindows` と同じ理由）。
    for (const window of monthWindows(change.effectiveDate, until)) {
      const remainingDays = inclusiveDays(window.from, window.to);
      if (remainingDays <= 0) continue;
      const { refund, charge, delta } = planChangeDelta({
        periodDays: window.monthDays,
        remainingDays,
        fromAmount: fromPrice.unitPrice * fromQuantity,
        toAmount: toPrice.unitPrice * toQuantity,
      });
      if (delta === 0) continue;
      const label =
        delta > 0
          ? `${toPrice.name} へ変更した差額（${window.from} 〜 ${window.to}・${remainingDays}/${window.monthDays} 日）`
          : `${toPrice.name} へ変更した前払い分の返金（${window.from} 〜 ${window.to}・${remainingDays}/${window.monthDays} 日）`;
      lines.push({
        kind: 'adjustment',
        description: label,
        quantityMilli: 1000,
        unitPrice: delta,
        amount: delta,
        metadata: {
          reason: 'plan_change_prepaid',
          effectiveDate: change.effectiveDate,
          // **支払い済みの状態**（差額の基準）。直前の区間のプランではない。
          fromPlanCode: paidPlanCode,
          toPlanCode: change.toPlanCode,
          fromQuantity,
          toQuantity,
          from: window.from,
          to: window.to,
          periodDays: window.monthDays,
          usedDays: remainingDays,
          refundedFromOldPlan: refund,
          chargedForNewPlan: charge,
        },
      });
    }
  }
  return lines;
}
